/**
 * Posições do diagrama. O `dagre` arruma bem grafo acíclico e mal ciclo, e o fluxo do chamado
 * tem ciclo (aguardando ↔ em atendimento, bloqueado ↔ em atendimento, reabrir). Por isso só as
 * arestas de AVANÇO entram no `dagre`; as de volta e as "de qualquer estado" são desenhadas por
 * cima, sem mexer no layout (spec 2026-10-26 C8).
 */
import dagre from '@dagrejs/dagre'
import type { AtorDoCaminho, CaminhoDoFluxo } from '@/api/chamados'
import { STATUS_DO_CHAMADO } from '@/components/chamados/StatusBadge'
import { ESTADO_ANTERIOR, QUALQUER_ESTADO, origens } from './visaoDoFluxo'

export const NO_LARGURA = 224
export const NO_ALTURA = 64
export const ID_QUALQUER = '__qualquer__'
export const ID_ANTERIOR = '__anterior__'

export type TipoDeAresta = 'avanco' | 'volta' | 'qualquer'

export interface ArestaDoFluxo {
  id: string
  de: string
  para: string
  tipo: TipoDeAresta
  ator: AtorDoCaminho
  caminho: CaminhoDoFluxo
}

export interface NoPosicionado { id: string; x: number; y: number }

const ordem = (estado: string) => {
  const i = STATUS_DO_CHAMADO.indexOf(estado as (typeof STATUS_DO_CHAMADO)[number])
  return i < 0 ? STATUS_DO_CHAMADO.length : i
}

/** Uma aresta por (origem, destino, caminho): `de` com vários estados vira várias. */
export function arestasDoFluxo(caminhos: CaminhoDoFluxo[], estados: string[]): ArestaDoFluxo[] {
  const arestas: ArestaDoFluxo[] = []
  caminhos.forEach((c, i) => {
    const para = c.para === ESTADO_ANTERIOR ? ID_ANTERIOR : c.para
    if (para !== ID_ANTERIOR && !estados.includes(para)) return
    for (const de of origens(c)) {
      if (de === QUALQUER_ESTADO) {
        arestas.push({ id: `c${i}-q`, de: ID_QUALQUER, para, tipo: 'qualquer', ator: c.ator, caminho: c })
        continue
      }
      if (!estados.includes(de)) continue
      const tipo: TipoDeAresta = para === ID_ANTERIOR || ordem(para) > ordem(de) ? 'avanco' : 'volta'
      arestas.push({ id: `c${i}-${de}`, de, para, tipo, ator: c.ator, caminho: c })
    }
  })
  return arestas
}

export function layoutDoFluxo(estados: string[], arestas: ArestaDoFluxo[]): NoPosicionado[] {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: 'LR', nodesep: 32, ranksep: 96 })
  const ids = [...estados]
  if (arestas.some((a) => a.de === ID_QUALQUER)) ids.unshift(ID_QUALQUER)
  if (arestas.some((a) => a.para === ID_ANTERIOR)) ids.push(ID_ANTERIOR)
  ids.forEach((id) => g.setNode(id, { width: NO_LARGURA, height: NO_ALTURA }))
  arestas.filter((a) => a.tipo === 'avanco').forEach((a) => g.setEdge(a.de, a.para))
  dagre.layout(g)
  return ids.map((id) => {
    const p = g.node(id)
    return { id, x: p.x - NO_LARGURA / 2, y: p.y - NO_ALTURA / 2 }
  })
}
